import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { DialogPromptApi, DialogPromptSpeakWithGrade } from '../../api/DialogPromptApi';
import ChatBubble from '../ChatBubble';

import AddSelfStudyBlueIcon from '../../asset/icon-add-self-study-blue.png';
import MicrophoneBlueIcon from '../../asset/icon-microphone-blue.png';
import RightArrowBlueIcon from '../../asset/icon-right-arrow-blue.png';
import AudioWaveIcon from '../../asset/icons8-audio-wave.gif';

import { useHistory, useLocation } from 'react-router-dom';
import { useAudioRecorder } from '../../AudioRecorder';
import { ScoreGrade, hasAnySpokenSentenceScore, sentenceScoreAllGood, sentenceScoreNonSilentFailed, sentenceScoreAnyFailed } from '../../Score';
import { AudioApi } from '../../api/AudioApi';
import { ISentenceScore } from '../../api/ScoreApi';
import { Sentence, SentenceApi } from '../../api/SentenceApi';
import { UserDialogPromptApi } from '../../api/UserDialogPromptApi';
import AppBarContext from '../../context/AppBarContext';
import { useAudioPlayer } from '../../context/AudioPlayerContext';
import DialogPromptFooterContext from '../../context/DialogPromptFooterContext';
import { scrollElementIntoViewWithFallback } from '../../util/scrollUtils';
import AddSentenceDialog from '../AddSentenceDialog';
import { handleErrorMessage } from '../ErrorDialog';
import { TeacherAudioActions, getSelectedTeacherSpeakerRole } from '../TeacherAudioReportButton';
import { SentenceScoreMediumResult, SentenceScoreTryAgain, SentenceScoreTryAgainNo, SentenceScoreTryAgainYes, getAudioByText } from './DialogPromptComponent';

interface DialogPromptSpeakWithGradeComponentProps {
    dialogPrompt: DialogPromptSpeakWithGrade;
    setDialogPromptComplete: (dialogPromptId: number) => void;
    showAvatar?: boolean;
    chatBubbleContainerRef: React.RefObject<HTMLDivElement>;
    isSettings?: boolean;
}


const DialogPromptSpeakWithGradeComponent: React.FC<DialogPromptSpeakWithGradeComponentProps> = ({ dialogPrompt, setDialogPromptComplete, showAvatar = true, chatBubbleContainerRef }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const { playAudio } = useAudioPlayer();

    const { setLeftNavigationList, setRightNavigationList, setActionList, clearFooterList } = React.useContext(DialogPromptFooterContext)

    const [teacherChinesePromptSentence, setTeacherChinesePromptSentence] = React.useState<Sentence | null>(null);

    const [promptAudio, setPromptAudio] = React.useState<Blob | null | undefined>(undefined);
    const [teacherChinesePromptAudio, setTeacherChinesePromptAudio] = React.useState<Blob | null | undefined>(undefined);
    const [, setStudentResponsePromptAudio] = React.useState<Blob | null | undefined>(undefined);
    const [teacherHintPromptAudio, setTeacherHintPromptAudio] = React.useState<Blob | null | undefined>(undefined);
    const [yesResponseAudio, setYesResponseAudio] = React.useState<Blob | null | undefined>(undefined);
    const [noResponseAudio, setNoResponseAudio] = React.useState<Blob | null | undefined>(undefined);
    const [sentenceScoreMediumResultAudio, setSentenceScoreMediumResultAudio] = React.useState<Blob | null | undefined>(undefined);
    const [sentenceScoreTryAgainAudio, setSentenceScoreTryAgainAudio] = React.useState<Blob | null | undefined>(undefined);
    const [, setSentenceScoreTryAgainYesAudio] = React.useState<Blob | null | undefined>(undefined);
    const [, setSentenceScoreTryAgainNoAudio] = React.useState<Blob | null | undefined>(undefined);

    React.useEffect(() => {
        clearFooterList()
        getAudioByText(history, setErrorMessage, dialogPrompt.prompt, setPromptAudio)
        getAudioByText(history, setErrorMessage, dialogPrompt.teacher_chinese_prompt, setTeacherChinesePromptAudio)
        getAudioByText(history, setErrorMessage, dialogPrompt.student_response_prompt, setStudentResponsePromptAudio)
        getAudioByText(history, setErrorMessage, dialogPrompt.teacher_hint_prompt, setTeacherHintPromptAudio)
        getAudioByText(history, setErrorMessage, dialogPrompt.yes_response, setYesResponseAudio)
        getAudioByText(history, setErrorMessage, dialogPrompt.no_response, setNoResponseAudio)
        getAudioByText(history, setErrorMessage, SentenceScoreMediumResult, setSentenceScoreMediumResultAudio)
        getAudioByText(history, setErrorMessage, SentenceScoreTryAgain, setSentenceScoreTryAgainAudio)
        getAudioByText(history, setErrorMessage, SentenceScoreTryAgainYes, setSentenceScoreTryAgainYesAudio)
        getAudioByText(history, setErrorMessage, SentenceScoreTryAgainNo, setSentenceScoreTryAgainNoAudio)
        if (dialogPrompt.teacher_chinese_prompt_sentence_id) {
            SentenceApi.getById(dialogPrompt.teacher_chinese_prompt_sentence_id)
                .then((response) => {
                    setTeacherChinesePromptSentence(response.data)
                })
                .catch(err => {
                    handleErrorMessage(err, setErrorMessage, history, location);
                })
        }
    }, [clearFooterList, dialogPrompt.no_response, dialogPrompt.prompt, dialogPrompt.student_response_prompt, dialogPrompt.teacher_chinese_prompt, dialogPrompt.teacher_chinese_prompt_sentence_id, dialogPrompt.teacher_hint_prompt, dialogPrompt.yes_response, history, location, setErrorMessage])

    const [teacherEnglishChatBubbleDone, setTeacherEnglishChatBubbleDone] = React.useState(false);
    const [teacherImageDone, setTeacherImageDone] = React.useState(false);

    const imageRef = React.useRef<HTMLImageElement>(null);

    const [imageSize, setImageSize] = React.useState<{ width: number; height: number } | null>(null);
    const [showLightbox, setShowLightbox] = React.useState(false);
    const [hovering, setHovering] = React.useState(false);

    const scrollToImage = () => {
        const handleImageLoad = () => {
            if (imageRef.current) {
                imageRef.current.scrollIntoView({ behavior: 'smooth' });
            }
            setTeacherImageDone(true);
        };

        if (imageRef.current) {
            if (imageRef.current.complete && imageRef.current.naturalHeight !== 0) {
                // If image is already loaded
                handleImageLoad();
            } else {
                // Image is not yet loaded, attach event listener
                imageRef.current.addEventListener('load', handleImageLoad);
            }
        }

        return () => {
            // console.log("dialogPrompt.image_src return")
            if (imageRef.current) {
                // console.log("dialogPrompt.image_src return removeEventListener")
                imageRef.current.removeEventListener('load', handleImageLoad);
            }
        };
    }

    React.useEffect(() => {
        if (teacherEnglishChatBubbleDone) {
            // console.log("teacherEnglishChatBubbleDone=true");
            if ((dialogPrompt.image_src && dialogPrompt.image_src != "broken") || (dialogPrompt.image_src_url && dialogPrompt.image_src_url != "broken")) {
                // console.log("dialogPrompt.image_src")
                // make sure to scroll after the image is loaded.
                return scrollToImage()
            } else {
                setTeacherImageDone(true);
            }
        }
    }, [teacherEnglishChatBubbleDone, dialogPrompt.image_src, dialogPrompt.image_src_url])

    const [teacherChineseChatBubbleDone, setTeacherChineseChatBubbleDone] = React.useState(false);

    const handleTeacherChinesePromptSpeakerClick = React.useCallback(() => {
        if (teacherChinesePromptSentence == null) {
            setTeacherChineseChatBubbleDone(true);
            return;
        }

        const audioPromise = AudioApi.getTeacherSentence(teacherChinesePromptSentence.id)

        audioPromise
            .then(response => {
                // check if the response was successful
                if (response.status >= 300) {
                    throw new Error('Getting male audio was not ok statuscode=' + response.status + " " + response.statusText);
                }
                return response.data;
            })
            .then(blob => {
                playAudio(blob, { onEnded: () => { setTeacherChineseChatBubbleDone(true) } })
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
    }, [teacherChinesePromptSentence, playAudio, setErrorMessage, history, location]);

    const teacherChineseAudioReportContext = React.useMemo(() => teacherChinesePromptSentence ? ({
        sentenceId: teacherChinesePromptSentence.id,
        dialogPromptId: dialogPrompt.id,
        textToSpeechSpeakerId: getSelectedTeacherSpeakerRole(),
        audioText: teacherChinesePromptSentence.text,
        audioRole: 'teacher_chinese_prompt',
    }) : null, [dialogPrompt.id, teacherChinesePromptSentence]);

    React.useEffect(() => {
        if (teacherImageDone) {
            // console.log("teacherImageDone=true");
            if (teacherChinesePromptSentence && teacherChinesePromptSentence.text) {
                setActionList([
                    <TeacherAudioActions
                        context={teacherChineseAudioReportContext}
                        onPlay={handleTeacherChinesePromptSpeakerClick}
                        speakerAriaLabel="Play sentence audio"
                        showReportMenu={false}
                    />
                ])
            } else {
                setTeacherChineseChatBubbleDone(true)
            }
        }
    }, [teacherImageDone, teacherChinesePromptSentence, handleTeacherChinesePromptSpeakerClick, setActionList, teacherChineseAudioReportContext])

    function isStudentResponsePromptEmpty(prompt: string | null) {
        return prompt == null || prompt == undefined || prompt.trim().toLowerCase() == "<answer/>"
    }

    React.useEffect(() => {
        if (teacherChineseChatBubbleDone) {
            if (isStudentResponsePromptEmpty(dialogPrompt.student_response_prompt)) {
                // skip student chat bubble if there is none
                setStudentChatBubbleDone(true);
            }
            // useEffect on teacherEnglishChatBubbleDone doesn't seem to be working
            return scrollToImage();
        }
    }, [teacherChineseChatBubbleDone, dialogPrompt.student_response_prompt])

    const [studentChatBubbleDone, setStudentChatBubbleDone] = React.useState(false);

    React.useEffect(() => {
        if (studentChatBubbleDone) {
            // useEffect on teacherEnglishChatBubbleDone doesn't seem to be working
            return scrollToImage();
        }
    }, [studentChatBubbleDone])

    const [studentCorrectResponseSentenceId, setStudentCorrectResponseSentenceId] = React.useState(-1);

    const [isScoring, setIsScoring] = React.useState(false);

    const [displayHintWithoutScore, setDisplayHintWithoutScore] = React.useState(false);

    const [sentenceScoreList, setSentenceScoreList] = React.useState<ISentenceScore[]>([]);
    const [chatBubbleSentenceScoreDone, setChatBubbleSentenceScoreDone] = React.useState<boolean[]>([]);
    const [chatBubbleTeacherResponseDone, setChatBubbleTeacherResponseDone] = React.useState<boolean[]>([]);
    const [tryAgainClickedList, setTryAgainClickedList] = React.useState<(boolean | null)[]>([]);

    const [chatBubbleTryAgainDone, setChatBubbleTryAgainDone] = React.useState(false);
    const [chatBubbleContinueDone, setChatBubbleContinueDone] = React.useState(false);

    const [studentAnswer, setStudentAnswer] = React.useState<string | null>('');

    const audioDataRef = React.useRef<Blob | null>(null);

    const { startRecording, stopRecording, isRecording } = useAudioRecorder({
        setErrorMessage,
        onStop: (audioData) => {
            setIsScoring(true)
            const formData = new FormData();
            formData.append('audio', audioData);
            audioDataRef.current = audioData

            DialogPromptApi.getDialogPromptSpeakWithGradeMatch(dialogPrompt.id, formData)
                .then((response) => {
                    setIsScoring(false)
                    const closestResponseScore = response.data
                    UserDialogPromptApi.addSpeakingAttempt(dialogPrompt.id, closestResponseScore.score_mean)
                        .catch(err => { handleErrorMessage(err, setErrorMessage, history, location); })
                    // console.log("handleAudioDone, displayHintWithoutScore= " + displayHintWithoutScore)

                    // wierd bug with DisplayHintWithoutScore that is updating async
                    // check withint setState so we have the correct value of DisplayHintWithoutScore
                    setDisplayHintWithoutScore(prevDisplayHint => {
                        // if text doesn't match, then show hint
                        if (closestResponseScore.passing_grade != ScoreGrade.Good && dialogPrompt.teacher_hint_prompt != null && prevDisplayHint == false) {
                            return true
                        } else { // else display 
                            closestResponseScore.audioData = audioData;
                            setStudentAnswer(closestResponseScore.text)
                            setStudentCorrectResponseSentenceId(closestResponseScore.sentence_id)
                            setSentenceScoreList((previous) => [...previous, closestResponseScore]);
                            return prevDisplayHint
                        }
                    });
                })
                .catch(err => {
                    handleErrorMessage(err, setErrorMessage, history, location);
                })
        }
    });

    const [shouldJiggleSkipArrow, setShouldJiggleSkipArrow] = React.useState(false);
    const jiggleKeyframes = React.useMemo(() => ({
        '@keyframes jiggle': {
            '0%': { transform: 'rotate(0deg)' },
            '12.5%': { transform: 'rotate(-20deg)' },
            '37.5%': { transform: 'rotate(20deg)' },
            '52.5%': { transform: 'rotate(-20deg)' },
            '77.5%': { transform: 'rotate(20deg)' },
            '100%': { transform: 'rotate(0)' },
        },
    }), []);
    const jiggleDuration = 500
    const jiggleIteration = 8

    const [openCancelDialog, setOpenCancelDialog] = React.useState(false);
    const [addSentenceDialogOpen, setAddSentenceDialogOpen] = React.useState(false);

    const handleCancelClick = React.useCallback(() => {
        setOpenCancelDialog(true);
    }, []);

    const updateTryAgainClickedList = React.useCallback((tryAgain: boolean) => {
        const index = sentenceScoreList.length - 1;
        setTryAgainClickedList(previous => {
            const updatedArray = [...previous];
            updatedArray[index] = tryAgain;
            return updatedArray;
        })
    }, [sentenceScoreList.length]);

    const handleNextWordClick = React.useCallback(() => {
        setChatBubbleTryAgainDone(false)
        updateTryAgainClickedList(false)
    }, [updateTryAgainClickedList]);

    const handleTryAgainClick = React.useCallback(() => {
        setChatBubbleTryAgainDone(false)
        updateTryAgainClickedList(true)
    }, [updateTryAgainClickedList]);

    const handleAddSentenceClick = React.useCallback(() => {
        setAddSentenceDialogOpen(true);
    }, []);
    React.useEffect(() => {
        if (sentenceScoreList.length === 0) return;

        setChatBubbleSentenceScoreDone(previous => {
            let changed = false;
            const updatedArray = [...previous];
            sentenceScoreList.forEach((_sentenceScore, index) => {
                if (updatedArray[index] !== true) {
                    updatedArray[index] = true;
                    changed = true;
                }
            });
            return changed ? updatedArray : previous;
        });

        const last = sentenceScoreList[sentenceScoreList.length - 1];
        if (!sentenceScoreNonSilentFailed(last)) {
            setShouldJiggleSkipArrow(false);
            return;
        }

        const failedCount = sentenceScoreList.reduce(
            (acc, sentenceScore) => acc + (sentenceScoreNonSilentFailed(sentenceScore) ? 1 : 0),
            0
        );

        if (failedCount % 2 === 0) {
            setShouldJiggleSkipArrow(true);
            const totalDuration = jiggleDuration * jiggleIteration
            const t = setTimeout(() => setShouldJiggleSkipArrow(false), totalDuration + 50); // length of the animation
            return () => clearTimeout(t);
        } else {
            setShouldJiggleSkipArrow(false);
        }
    }, [sentenceScoreList]);

    React.useEffect(() => {
        if (studentChatBubbleDone && chatBubbleTryAgainDone == false) {
            if (sentenceScoreList.length == 0 || sentenceScoreAnyFailed(sentenceScoreList[sentenceScoreList.length - 1]) || tryAgainClickedList[sentenceScoreList.length - 1]) {
                if (hasAnySpokenSentenceScore(sentenceScoreList)) {
                    setLeftNavigationList([
                        <IconButton
                            onClick={handleCancelClick}
                            sx={{
                                ...jiggleKeyframes,
                                animation: shouldJiggleSkipArrow ? `jiggle ${jiggleDuration}ms ease-in-out ${jiggleIteration}` : 'none',
                            }}
                        >
                            <img src={RightArrowBlueIcon} style={{ height: '100%', width: 'auto', maxHeight: '24px' }} />
                        </IconButton>
                    ])
                }
                if (studentCorrectResponseSentenceId >= 0) {
                    setRightNavigationList([
                        <Tooltip title="Add to Pronunciation" arrow>
                            <IconButton onClick={handleAddSentenceClick} sx={{ alignSelf: 'center' }}>
                                <img src={AddSelfStudyBlueIcon} style={{ height: '100%', width: 'auto', maxHeight: '24px' }} />
                            </IconButton>
                        </Tooltip>
                    ])
                }
                setActionList([
                    <IconButton
                        title={isRecording ? 'Stop recording' : 'Start recording'}
                        onClick={isRecording ? stopRecording : startRecording}
                    >
                        {isRecording ? <img src={AudioWaveIcon} alt="Recording" style={{ height: '100%', width: 'auto', maxHeight: '36px' }} />
                            : isScoring ? <CircularProgress size={24} sx={{ height: '100%', width: 'auto', maxHeight: '36px' }} />
                                : <img src={MicrophoneBlueIcon} style={{ height: '100%', width: 'auto', maxHeight: '36px' }} />}
                    </IconButton>
                ])
            }
        }
    }, [
        studentChatBubbleDone, sentenceScoreList, isRecording, isScoring,
        chatBubbleTryAgainDone, tryAgainClickedList, shouldJiggleSkipArrow, jiggleKeyframes,
        setActionList, setLeftNavigationList, setRightNavigationList,
        startRecording, stopRecording, studentCorrectResponseSentenceId,
        handleCancelClick, handleAddSentenceClick,
    ]);

    React.useEffect(() => {
        if (chatBubbleContainerRef.current) {
            scrollElementIntoViewWithFallback(chatBubbleContainerRef.current);
            // console.log("sentenceScoreList, scrollIntoView")
        }
    }, [sentenceScoreList, chatBubbleContainerRef]);

    // const mediaRecorderRef = React.useRef<MediaRecorder | null>(null);

    // let audioData: Blob | null = null;

    // const startRecording = () => {
    //     navigator.mediaDevices.getUserMedia({ audio: true })
    //         .then((stream) => {
    //             // console.log("startRecording() 1")
    //             mediaRecorderRef.current = new MediaRecorder(stream);
    //             mediaRecorderRef.current.addEventListener('dataavailable', handleAudioDataAvailable);
    //             mediaRecorderRef.current.addEventListener('stop', handleAudioDone);

    //             mediaRecorderRef.current.start();

    //             setIsRecording(true);
    //             // console.log("startRecording() 2")
    //         })
    //         .catch(err => {
    //             handleErrorMessage(err, setErrorMessage, history, location);
    //         });
    // };
    // const stopRecording = () => {
    //     if (mediaRecorderRef.current) {
    //         // console.log("stopRecording() 1")
    //         mediaRecorderRef.current.stop();
    //         mediaRecorderRef.current.removeEventListener('dataavailable', handleAudioDataAvailable);
    //         mediaRecorderRef.current.removeEventListener('stop', handleAudioDone);
    //         // console.log("stopRecording() 2")
    //     }

    //     setIsRecording(false);
    // };

    // const handleAudioDataAvailable = (event: BlobEvent) => {
    //     // console.log("handleAudioDataAvailable() 1")
    //     if (event.data.size > 0) {
    //         // console.log("event.data.size > 0")
    //         audioData = event.data;
    //     }
    // };

    // const handleAudioDone = () => {
    //     // console.log("handleAudioDone() 1")
    //     if (audioData) {
    //         // console.log("handleAudioDone() has audio data 1")
    //         setIsScoring(true)
    //         const formData = new FormData();
    //         formData.append('audio', audioData);

    //         // console.log("handle audio done")

    //         DialogPromptApi.getDialogPromptSpeakWithGradeMatch(dialogPrompt.id, formData)
    //             .then((response) => {
    //                 setIsScoring(false)
    //                 const closestResponseScore = response.data
    //                 // console.log("handleAudioDone, displayHintWithoutScore= " + displayHintWithoutScore)

    //                 // wierd bug with DisplayHintWithoutScore that is updating async
    //                 // check withint setState so we have the correct value of DisplayHintWithoutScore
    //                 setDisplayHintWithoutScore(prevDisplayHint => {
    //                     // if text doesn't match, then show hint
    //                     if (closestResponseScore.pinyin_list_spoken != closestResponseScore.pinyin_list_transcript && dialogPrompt.teacher_hint_prompt != null && prevDisplayHint == false) {
    //                         return true
    //                     } else { // else display 
    //                         closestResponseScore.audioData = audioData;
    //                         setStudentAnswer(closestResponseScore.text)
    //                         setStudentCorrectResponseSentenceId(closestResponseScore.sentence_id)
    //                         setSentenceScoreList((previous) => [...previous, closestResponseScore]);
    //                         return prevDisplayHint
    //                     }
    //                 });
    //             })
    //             .catch(err => {
    //                 handleErrorMessage(err, setErrorMessage, history, location);
    //             })
    //     }
    // };

    // React.useEffect(() => {
    //     if (studentCorrectResponseSentenceId > 0 && audioDataRef.current) {
    //         const formData = new FormData();
    //         formData.append('audio', audioDataRef.current);
    //         ScoreApi.getScore(studentCorrectResponseSentenceId, formData)
    //             .then(response => {
    //                 setIsScoring(false)
    //                 // check if the response was successful
    //                 if (response.status >= 300) {
    //                     throw new Error('Scoring audio was not ok statuscode=' + response.status + " " + response.statusText);
    //                 }
    //                 return response.data;
    //             })
    //             .then(responseJson => {
    //                 responseJson.audioData = audioDataRef.current;
    //                 setSentenceScoreList((previous) => [...previous, responseJson]);
    //             })
    //             .catch(err => {
    //                 handleErrorMessage(err, setErrorMessage, history, location);
    //             })
    //     }
    // }, [studentCorrectResponseSentenceId])

    /**
     * try again
     */

    React.useEffect(() => {
        if (chatBubbleTryAgainDone) {
            setLeftNavigationList([
                <IconButton onClick={handleCancelClick}>
                    <img src={RightArrowBlueIcon} style={{ height: '100%', width: 'auto', maxHeight: '24px' }} />
                </IconButton>
            ])
            setActionList([
                <Button variant="contained" onClick={handleNextWordClick} className="secondaryButton" >
                    No, move on
                </Button>,
                <Button variant="contained" onClick={handleTryAgainClick} >
                    Yes, try again
                </Button>
            ])
        }
    }, [chatBubbleTryAgainDone, handleNextWordClick, handleTryAgainClick, setActionList, setLeftNavigationList, handleCancelClick]);

    /**
     *  skip cancel
     */

    const handleCancelDialogConfirm = () => {
        setOpenCancelDialog(false);
        setChatBubbleContinueDone(true);
    };

    const handleCancelDialogCancel = () => {
        setOpenCancelDialog(false);
    };

    React.useEffect(() => {
        if (chatBubbleContinueDone) {
            clearFooterList()
            setDialogPromptComplete(dialogPrompt.id)

        }
    }, [chatBubbleContinueDone, clearFooterList, dialogPrompt.id, setDialogPromptComplete])

    /**
     * add sentence dialog
     */

    const [, setAddSentenceSuccessMessage] = React.useState('');

    const handleClose = () => {
        setAddSentenceDialogOpen(false);
        setAddSentenceSuccessMessage('')
    };

    const studentCorrectResponseSentence = dialogPrompt.dialog_prompt_sentence_list.find(
        (sentence) => sentence.sentence_id === studentCorrectResponseSentenceId
    );
    const studentCorrectResponseSentenceText =
        studentCorrectResponseSentence?.student_response ||
        studentCorrectResponseSentence?.sentence_text ||
        studentAnswer ||
        '';

    return (
        <Stack>
            <ChatBubble
                isTeacher={dialogPrompt.is_teacher}
                message={dialogPrompt.prompt}
                messageAudio={promptAudio ? promptAudio : null}
                showAvatar={showAvatar}
                chatBubbleContainerRef={chatBubbleContainerRef}
                setChatBubbleDone={setTeacherEnglishChatBubbleDone}
            />
            {(teacherEnglishChatBubbleDone && (dialogPrompt.image_src || dialogPrompt.image_src_url)) && (<>
                <div style={{ position: 'relative', display: 'inline-block', margin: '0 auto', marginBottom: '16px' }}>
                    <img
                        ref={imageRef}
                        src={dialogPrompt.image_src ? dialogPrompt.image_src : (dialogPrompt.image_src_url ? dialogPrompt.image_src_url : undefined)}
                        alt={dialogPrompt.teacher_chinese_prompt ? dialogPrompt.teacher_chinese_prompt : ""}
                        onClick={() => setShowLightbox(true)}
                        onMouseEnter={() => setHovering(true)}
                        onMouseLeave={() => setHovering(false)}
                        onLoad={(e) => {
                            const img = e.currentTarget;
                            setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
                        }}
                        style={{
                            maxHeight: '200px', maxWidth: '200px',
                            height: 'auto', width: 'auto',
                            display: 'block',
                            cursor: 'pointer',
                            transition: 'opacity 0.2s ease',
                            opacity: hovering ? 0.85 : 1,
                            borderRadius: '10px',
                        }}
                    />
                    {hovering && imageSize && (
                        <div style={{
                            position: 'absolute', bottom: '8px', right: '8px',
                            backgroundColor: 'rgba(0, 0, 0, 0.7)', color: '#fff',
                            padding: '4px 8px', borderRadius: '4px',
                            fontSize: '12px', pointerEvents: 'none', zIndex: 10,
                        }}>
                            {imageSize.width} × {imageSize.height}
                        </div>
                    )}
                </div>
            </>)}
            {(teacherImageDone && teacherChinesePromptSentence && teacherChinesePromptSentence.text) && (<>
                <ChatBubble
                    isTeacher={true}
                    message={teacherChinesePromptSentence && teacherChinesePromptSentence.text ? teacherChinesePromptSentence.text : ''}
                    messageAudio={teacherChinesePromptAudio ? teacherChinesePromptAudio : null}
                    listenSentenceId={teacherChinesePromptSentence && teacherChinesePromptSentence.text ? teacherChinesePromptSentence.id : undefined}
                    showAvatar={showAvatar}
                    chatBubbleContainerRef={chatBubbleContainerRef}
                    // setChatBubbleDone={setTeacherChineseChatBubbleDone}  // move to end of audio play
                    useChineseFont={true}
                />
            </>)}
            {(teacherChineseChatBubbleDone && !isStudentResponsePromptEmpty(dialogPrompt.student_response_prompt)) && (
                <ChatBubble
                    isTeacher={false}
                    message={dialogPrompt.student_response_prompt ? dialogPrompt.student_response_prompt : ""}
                    messageAudio={null} // no audio for student
                    showAvatar={showAvatar}
                    chatBubbleContainerRef={chatBubbleContainerRef}
                    setChatBubbleDone={setStudentChatBubbleDone}
                    useChineseFont={true}
                />
            )}
            {(displayHintWithoutScore && dialogPrompt.teacher_hint_prompt != null) && (
                <ChatBubble
                    isTeacher={true}
                    message={dialogPrompt.teacher_hint_prompt}
                    messageAudio={teacherHintPromptAudio ? teacherHintPromptAudio : null}
                    showAvatar={showAvatar}
                    chatBubbleContainerRef={chatBubbleContainerRef}
                    useChineseFont={true}
                />
            )}

            {sentenceScoreList.map((sentenceScore, index) => (
                <div key={index}>
                    <ChatBubble
                        isTeacher={false}
                        message={sentenceScore.text}
                        messageAudio={null} // audio will be found through sentenceScore
                        sentenceScore={sentenceScore}
                        showAvatar={showAvatar}
                        chatBubbleContainerRef={chatBubbleContainerRef}
                        setChatBubbleDone={(done) => setChatBubbleSentenceScoreDone(previous => {
                            // console.log("sentene score updated index " + index)
                            if (previous[index] === done) return previous;
                            const updatedArray = [...previous];
                            updatedArray[index] = done;
                            return updatedArray;
                        })}
                    />
                    {(chatBubbleSentenceScoreDone[index] || sentenceScoreAnyFailed(sentenceScore)) && (<>
                        {sentenceScoreAllGood(sentenceScore) ? (
                            <ChatBubble
                                isTeacher={true}
                                message={dialogPrompt.yes_response}
                                messageAudio={yesResponseAudio ? yesResponseAudio : null}
                                showAvatar={showAvatar}
                                chatBubbleContainerRef={chatBubbleContainerRef}
                                setChatBubbleDone={setChatBubbleContinueDone}
                            />
                        ) : sentenceScoreAnyFailed(sentenceScore) ? (
                            <ChatBubble isTeacher={dialogPrompt.is_teacher}
                                message={dialogPrompt.no_response}
                                messageAudio={noResponseAudio ? noResponseAudio : null}
                                showAvatar={showAvatar}
                                chatBubbleContainerRef={chatBubbleContainerRef}
                                setChatBubbleDone={() => {
                                    setChatBubbleTryAgainDone(false);
                                    // set null, to skip this index
                                    setTryAgainClickedList((previous) => {
                                        if (previous[index] === null) return previous;
                                        const updatedArray = [...previous];
                                        updatedArray[index] = null;
                                        return updatedArray;
                                    });
                                }}
                            />
                        ) : (
                            <ChatBubble isTeacher={dialogPrompt.is_teacher}
                                message={SentenceScoreMediumResult}
                                messageAudio={sentenceScoreMediumResultAudio ? sentenceScoreMediumResultAudio : null}
                                showAvatar={showAvatar}
                                chatBubbleContainerRef={chatBubbleContainerRef}
                                setChatBubbleDone={(done) => setChatBubbleTeacherResponseDone(previous => {
                                    if (previous[index] === done) return previous;
                                    const updatedArray = [...previous];
                                    updatedArray[index] = done;
                                    return updatedArray;
                                })}
                            />
                        )}
                        {chatBubbleTeacherResponseDone[index] && (
                            <ChatBubble
                                isTeacher={true}
                                message={SentenceScoreTryAgain}
                                messageAudio={sentenceScoreTryAgainAudio ? sentenceScoreTryAgainAudio : null}
                                showAvatar={showAvatar}
                                chatBubbleContainerRef={chatBubbleContainerRef}
                                setChatBubbleDone={setChatBubbleTryAgainDone}
                            />
                        )}
                        {tryAgainClickedList[index] === true && (
                            <ChatBubble
                                isTeacher={false}
                                message={SentenceScoreTryAgainYes}
                                messageAudio={null} // no audio for student
                                showAvatar={showAvatar}
                                chatBubbleContainerRef={chatBubbleContainerRef}
                            />
                        )}
                        {tryAgainClickedList[index] === false && (
                            <ChatBubble
                                isTeacher={false}
                                message={SentenceScoreTryAgainNo}
                                messageAudio={null} // no audio for student
                                showAvatar={showAvatar}
                                chatBubbleContainerRef={chatBubbleContainerRef}
                                setChatBubbleDone={setChatBubbleContinueDone}
                            />
                        )}
                    </>)}
                </div>
            ))}
            {/* Lightbox */}
            {showLightbox && (
                <div
                    aria-label="Close image preview"
                    onClick={() => setShowLightbox(false)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            setShowLightbox(false);
                        }
                    }}
                    role="button"
                    tabIndex={0}
                    style={{
                        position: 'fixed', top: 0, left: 0,
                        width: '100vw', height: '100vh',
                        backgroundColor: 'rgba(0, 0, 0, 0.85)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', zIndex: 9999, margin: 0, padding: 0,
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px', cursor: 'default' }}
                    >
                        <img
                            src={dialogPrompt.image_src ? dialogPrompt.image_src : (dialogPrompt.image_src_url ? dialogPrompt.image_src_url : undefined)}
                            alt={dialogPrompt.teacher_chinese_prompt ? dialogPrompt.teacher_chinese_prompt : ""}
                            onLoad={(e) => {
                                const img = e.currentTarget;
                                setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
                            }}
                            style={{
                                maxWidth: '90vw', maxHeight: '90vh',
                                objectFit: 'contain', borderRadius: '10px',
                                boxShadow: '0 4px 24px rgba(0, 0, 0, 0.5)',
                            }}
                        />
                        {imageSize && (
                            <div style={{ color: '#ccc', fontSize: '12px', paddingRight: '4px' }}>
                                {imageSize.width} × {imageSize.height}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Confirmation Dialog */}
            <Dialog open={openCancelDialog} onClose={handleCancelDialogCancel}>
                <DialogTitle><Typography variant="h4" sx={{ color: '#383838' }}>Confirm Skip</Typography></DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: '#8D8D8D' }}>
                        Are you sure you want to skip this exercise?
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'space-between', '& .MuiButton-root': { flex: 1 } }}>
                    <Button onClick={handleCancelDialogCancel} color="primary" sx={{ color: '#787878' }}>
                        Keep trying
                    </Button>
                    <Button onClick={handleCancelDialogConfirm} color="primary" sx={{ color: '#787878' }}>
                        Skip
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Add Sentence Confirmation Dialog */}
            <AddSentenceDialog
                open={addSentenceDialogOpen}
                sentence={
                    studentCorrectResponseSentenceId > 0
                    ? { id: studentCorrectResponseSentenceId, text: studentCorrectResponseSentenceText }
                    : undefined
                }                
                onClose={handleClose}
            />
        </Stack >
    );
};

export default DialogPromptSpeakWithGradeComponent;
